import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {v4 as uuidv4} from 'uuid';
import {config} from "../utils/config";
import Cart from "../models/cart";
import { onSuccessHandler, onErrorHandler, addCouponSuccessHandler} from "./handlers";
import { CartOperation } from '../enums/cartOperation';
import { setCartOperation, setCoupon, setCurrentOperation, setIsShippingDisc, setRedirectCartToHomePage, setUserState, setValidatedCoupon, setWaitForRecalculate, startLoading, stopLoading } from './application';
import { LineItemType } from '../enums/lineItemType';
import { CartStatus, UserStatus } from '../enums/cartStatus';
import { set as setCookie , delete_cookie, get as getCookie, getBrandKeywordCampaignParameters } from '../utils/cookies';
import { redirectToURL } from '../utils/url';
import { Page } from '../enums/page';
import { RootState } from '.';
import { CouponState } from '../enums/coupon';
import { calculateThresholdStatus } from '../utils/cart';
import { applyPromoEvent, errorTrackingGaEvent } from '../utils/analytics/google';
import { discountApi } from './discount';
import { ErrorStatus } from '../enums/errorStatus';
import { mcpCustomWrapperApi } from './mcpCustomWrapper';
import { CouponStatus } from '../enums/couponStatus';

declare const window: any;
const xCorrelationId = uuidv4();
let recalculateRetryCount: number = 1;

export const newCartApi = createApi({
    reducerPath: 'newCartApi',
    baseQuery: fetchBaseQuery({
      baseUrl:  window.Config?.LAMBDA_CART_NEW ?? "https://npr-carts.api.pens.com/",
      prepareHeaders: (headers) => {
            headers.set(window.Config?.NP_API_KEY_HEADER_NAME ?? "x-api-key", window.Config?.NP_API_KEY_CLIENT_JS ?? "NSjMi38PBJ6s1rvNMA9FKO6FRn2iDd95bEDoaij8");
            headers.set("x-correlation-id" , xCorrelationId);
            headers.set("locale" , config('locale'));
            headers.set("addeffectiveprice" , 'true');
            return headers
        },
    }),
    tagTypes: ['newCart'],
    endpoints: (builder) => ({

      getCartByCartId: builder.query({
        async queryFn({cartId, userId, isLoggedIn , priceFlag = true , productFlag = true , discountFlag = true , taxFlag = true , shippingFlag = true}, {dispatch}, _extraOptions, baseQuery) 
        {
          if(cartId){
            try{
              const cartByCartId:any = await baseQuery({ 
                        url: `v1/carts/${cartId}/recalculate`, 
                        method: 'PUT',
                        params : { price: priceFlag, product: productFlag, discount: discountFlag, tax: taxFlag, shipping: shippingFlag, inventory: true }
                      });
              if(cartByCartId?.data?.errors?.[0]?.code === 422){     // Cart is empty and has no line items
                throw {error: cartByCartId?.data?.errors[0]}
              }

              if(cartByCartId?.error?.status === 400 && cartByCartId?.error?.data?.errors?.[0]?.code === 417){    // Locale Mismatch
                const payload = { isLogged: isLoggedIn, locale: config('locale'), mini: true};
                const cartByUserId:any = await dispatch(newCartApi.endpoints.getCartForCustomer.initiate({ userId: userId, ...payload })); // Get cart by user id for current locale
                  if(cartByUserId?.data?.data?.attributes){
                    const res2 = cartByUserId.data.data.attributes;
                    const cartByNewCartId:any = await baseQuery({ 
                      url: `v1/carts/${res2.id}/recalculate`, 
                      method: 'PUT',
                      params : { price: priceFlag, product: productFlag, discount: discountFlag, tax: taxFlag, shipping: shippingFlag, inventory: true }
                    });
                    dispatch(setUserState({isLoggedIn, userId, cartId: res2.id}))
                    setCookie('np.cartId',res2.id, 30, "/");
                    const res3 = cartByNewCartId?.data?.data?.attributes;
                    return { data: new Cart(res3)};
                  }else{
                    dispatch(setRedirectCartToHomePage(true));
                    return {error: cartByUserId?.error};
                  }
              }
              if(cartByCartId?.data?.data?.attributes){
                  const cart = cartByCartId.data.data.attributes;
                  if(cart?.cartState !== CartStatus.ACTIVE){
                    delete_cookie('np.cartId');
                    redirectToURL(`/${config('locale_details').current_sitename}/`);
                  }
                  return { data: new Cart(cart)};
              }
              return cartByCartId;
            }
            catch(error){
              return error;
            }
          }
          else{
              delete_cookie('np.cartId');
          }
        },
        async onQueryStarted(args, { dispatch, getState, queryFulfilled }) {
            try{
                const {data} = await queryFulfilled;
                const {applicationState: {user, currentPage,ongoingAction,coupon,doesCouponThresholdExist,isShippingDisc}}: any = getState();
                if(data && (currentPage === Page.CHECKOUTPAYMENT)){
                  dispatch(setWaitForRecalculate(false));
                }
                dispatch(onSuccessHandler(CartOperation.GETCART, {}));
                //handle operations on 504 of apply coupon
                if (data && ongoingAction?.name === CartOperation.APPLYCOUPON) {
                  const { isThresholdMet } = calculateThresholdStatus(data, coupon?.thresholdValue);
                  
                  //delete np.brandKeywordCampaign cookie if its a non-shipping target coupon without threshold
                  if(!(isShippingDisc || data.validDiscountCode || (doesCouponThresholdExist && !isThresholdMet))){
                    delete_cookie('np.brandKeywordCampaign'); 
                  }
                  const isEligibleForDiscount =
                    data?.discountExists ||
                    (!data?.shippingCustomItem && coupon?.thresholdValue && isThresholdMet && isShippingDisc) ||
                    (!data?.shippingCustomItem && !coupon?.thresholdValue && isShippingDisc);
                
                    if (!ongoingAction?.isAutoApplyCall && currentPage === Page.SHOPPINGBAG) {
                      const baseValue = ongoingAction?.isCouponManuallyApplied ? 'manual' : 'direct';
                      const eventValue = isEligibleForDiscount 
                          ? `success_${baseValue}` 
                          : `fail_${baseValue}_incorrect_products`;
                  
                      applyPromoEvent({ coupon: coupon?.code, event_value: eventValue });
                  }

                  dispatch(setCoupon({
                    state: CouponState.INELIGIBLE,
                    code: coupon.code,
                    description: coupon.description,
                    error: coupon.error,
                    details: coupon?.details || '',
                    thresholdValue: coupon?.thresholdValue || null,
                  }));
                
                  dispatch(stopLoading());
                }
                //handle operations when we skip applycoupon call when threshold is not met
                if(data && ongoingAction?.name === CartOperation.SKIPAPPLYCOUPON) {
                  const failureValue = ongoingAction?.isCouponManuallyApplied ? CouponStatus.FAIL_MANUAL_INSUFFICIENT_CART_VALUE : CouponStatus.FAIL_DIRECT_INSUFFICIENT_CART_VALUE;
                  if (currentPage === Page.SHOPPINGBAG && !ongoingAction?.isAutoApplyCall) {
                    applyPromoEvent({ coupon: coupon?.code, event_value: failureValue });
                  }
                  dispatch(stopLoading());
                }

                // update anonymousId id in cart incase its mismatch with the MCP anonymousId
                if(user.isLoggedIn === 'false' && user.userId !== data?.anonymousId && !data?.customerId){
                  const payLoad = {
                    "anonymousId": data.anonymousId,
                    "mcpAnonymousId": user.userId,
                  }
                  dispatch(mcpCustomWrapperApi.endpoints.updateMcpAnonymousId.initiate({data: payLoad}))
                }

                // Update customer email in cart if customerEmail is not set in cart for loggedIn users
                if (user.isLoggedIn === 'true' && 
                  user.email && 
                  (data?.customerEmail !== user.email || !data.emailDomain)) {
                  
                    const actions = [{
                      "action": "customerEmail",
                      "value": user.email
                    }];
                    
                    dispatch(newCartApi.endpoints.updateCart.initiate({
                      cartId: data.id,
                      actions: actions,
                      setUserType: true
                    }));
                }
                
                recalculateRetryCount = 1;
            }
            catch(error:any){
              if(error?.error?.status === ErrorStatus.FETCH_ERROR && recalculateRetryCount > 0) {
                recalculateRetryCount--;
                dispatch(newCartApi.util.invalidateTags(['newCart']));
              } else {
                const errorObj = {cartId : args.cartId, userId: args.userId, isLoggedIn: args.isLoggedIn, error}
                console.log("Error while fetching cart from getCartByCartId: ",errorObj);
                if((error?.error?.status === 400 && error?.error?.data?.errors?.[0]?.code === 417) || error?.error?.code === 422 ) dispatch(onErrorHandler({operation: CartOperation.GETCART, err: JSON.stringify(errorObj), errMsg: 'Error: while fetching cart', api: 'v1/carts/:cartId/recalculate', skipSentryLog: true}));
                else dispatch(onErrorHandler({operation: CartOperation.GETCART, err: JSON.stringify(errorObj), errMsg: 'Error: while fetching cart', api: 'v1/carts/:cartId/recalculate'}));
              }
            }
        },
        providesTags: ['newCart']
      }),
      
      markCartInvisible: builder.mutation({
        query: ({ cartId, userId, isLoggedIn }) => ({
          url: `v1/carts/${cartId}/invisible`,
          method: 'PUT',
        }),
        async onQueryStarted(args, { dispatch, queryFulfilled }) {
            try{
                const {data} = await queryFulfilled;
                return data;
            }
            catch(error){
                console.log(error);
            }
        }
      }),

      addUpsell: builder.mutation({
        async queryFn({cartId, mainLineItemId}, {dispatch, getState}, _extraOptions,  baseQuery) {
          const data:any = await baseQuery({ url: `v1/carts/${cartId}/lineitems`, method: 'POST', body: {
            "data": {
                "type": "Upsell",
                "attributes": {
                    "mainItemId": mainLineItemId
                }
            }
        }});
          return data;
        },
        async onQueryStarted({cartId, mainLineItemId}, { dispatch, getState, queryFulfilled }) {
            try{
              const {data} = await queryFulfilled;
              const {applicationState: {user}}: any = getState();
              const oldCart = getCart(getState() as any, {cartId: user.cartId, userId: user.userId, isLoggedIn: user.isLoggedIn})?.data ?? {};
              let updatedCart = new Cart(data?.data?.attributes);
              const upsell = updatedCart?.lineItems[mainLineItemId].find(lineitem => lineitem?.type === LineItemType.UPSELL);
              dispatch(newCartApi.util.updateQueryData('getCartByCartId', {cartId: user.cartId, userId: user.userId, isLoggedIn: user.isLoggedIn}, (draft) => Object.assign({}, updatedCart)));
              if (upsell?.data?.id) {
                window.pdi.overridePDI('[Up-sell]{currPageName}_additional-quantity', -1, upsell?.data?.id);
              }
              let specialOfferCookie = getBrandKeywordCampaignParameters(getCookie('np.brandKeywordCampaign')).couponCode;
              if(!specialOfferCookie){
                dispatch(setValidatedCoupon({ state: CouponState.INITIAL}))
              }
                if(oldCart?.validDiscountCode || specialOfferCookie){
                  dispatch(setCurrentOperation({name : CartOperation.ADDUPSELL, mainItemId : mainLineItemId}))
                  await dispatch(discountApi.endpoints.validateCoupon.initiate({ couponCode: specialOfferCookie || oldCart?.validDiscountCode , 'locale': config('locale') , callApplyCoupon: true}));
                }
                dispatch(setCartOperation({mainItemId : mainLineItemId}));
                dispatch(onSuccessHandler(CartOperation.ADDUPSELL, {mainItemId: mainLineItemId, upsellLineItemId: upsell.data.id}));
            }catch(error){
              console.log(error);
              dispatch(setCartOperation({mainItemId : mainLineItemId}));
              dispatch(onErrorHandler({operation: CartOperation.ADDUPSELL, err: JSON.stringify(error), errMsg: 'Error: while adding upsell', api: 'v1/carts/{cartId}/lineitems', mainItemId: mainLineItemId}));
          }
        }
      }),

      removeItem: builder.mutation({
        async queryFn({cartId, lineItemId, lineItemType, mainItemId}, {dispatch, getState}, _extraOptions,  baseQuery) {
          const response: any = await baseQuery({
            url: `v1/carts/${cartId}/removeitem`, method: 'DELETE', body: {
              "data": {
                "type": "RemoveItem",
                "attributes": {
                  "lineItemId": lineItemId
                }
              }
            }
          });
          return response;
        },
        async onQueryStarted(args, { dispatch, getState, queryFulfilled }) {
            try{
                const {data} = await queryFulfilled;
                const {applicationState: {user}}: any = getState();
                let oldCart:any = getCart(getState() as any, {cartId: user.cartId, userId: user.userId, isLoggedIn: user.isLoggedIn})?.data ?? {};
                let updatedCart:any = new Cart(data?.data?.attributes)?? {};
                dispatch(newCartApi.util.updateQueryData('getCartByCartId', {cartId: user.cartId, userId: user.userId, isLoggedIn: user.isLoggedIn}, (draft) => Object.assign({}, updatedCart)));
                let specialOfferCookie = getBrandKeywordCampaignParameters(getCookie('np.brandKeywordCampaign')).couponCode;
                if(!specialOfferCookie){
                  dispatch(setValidatedCoupon({ state: CouponState.INITIAL}))
                }
                if(oldCart?.validDiscountCode || specialOfferCookie){
                  dispatch(setCurrentOperation({name : CartOperation.REMOVEITEM}))
                  await dispatch(discountApi.endpoints.validateCoupon.initiate({ couponCode: specialOfferCookie || oldCart?.validDiscountCode , 'locale': config('locale') , callApplyCoupon: true}));
                }
                dispatch(setCartOperation({mainItemId : args.lineItemId}));
                dispatch(onSuccessHandler(CartOperation.REMOVEITEM, {}));
            }catch(error){
              console.log(error);
              dispatch(setCartOperation({mainItemId : args.lineItemId}));
              dispatch(onErrorHandler({operation: CartOperation.REMOVEITEM, err: JSON.stringify(error), errMsg: 'Error: while removing item', api: 'v1/cart/:cartId/removeitem', mainItemId: args.lineItemId})); 
              if(args?.lineItemType === LineItemType.ACCESSORY || args?.lineItemType === LineItemType.UPSELL){
                if(!config('isNASite')) window.location.reload();
              }   
            }
        }
      }),

      removeManual: builder.mutation({
        async queryFn({ cartId, lineItemId, additionalChargeCode }, { dispatch, getState }, _extraOptions, baseQuery) {
          const response: any = await baseQuery({
            url: `v1/carts/${cartId}/removecharge`, method: 'DELETE', body: {
              "data": {
                "type": "RemoveCharge",
                "attributes": {
                  "mainItemId": lineItemId,
                  "Charge": "AdditionalCharge",
                  "Code": additionalChargeCode
                }
              }
            }
          });
          return response;
        },
        async onQueryStarted(args, {dispatch, getState, queryFulfilled }) {
            try{
              const {data} = await queryFulfilled;
              const {applicationState: {user}}: any = getState();
              const oldCart = getCart(getState() as any, {cartId: user.cartId, userId: user.userId, isLoggedIn: user.isLoggedIn})?.data ?? {};
              let updatedCart = new Cart(data?.data?.attributes);
              dispatch(newCartApi.util.updateQueryData('getCartByCartId', {cartId: user.cartId, userId: user.userId, isLoggedIn: user.isLoggedIn}, (draft) => Object.assign({}, updatedCart)));
              let specialOfferCookie = getBrandKeywordCampaignParameters(getCookie('np.brandKeywordCampaign')).couponCode;  
              if(!specialOfferCookie){
                dispatch(setValidatedCoupon({ state: CouponState.INITIAL}))
              }
              if(oldCart?.validDiscountCode || specialOfferCookie){
                await dispatch(discountApi.endpoints.validateCoupon.initiate({ couponCode: specialOfferCookie || oldCart?.validDiscountCode , 'locale': config('locale') , callApplyCoupon: true}));
                }
                dispatch(setCartOperation({mainItemId : args.lineItemId}));
                dispatch(onSuccessHandler(CartOperation.REMOVEMANUAL, {}));
            }catch(error){
              console.log(error);
              dispatch(setCartOperation({mainItemId : args.lineItemId}));
              dispatch(onErrorHandler({operation: CartOperation.REMOVEMANUAL, err: JSON.stringify(error), errMsg: 'Error: while removing manual charge', api: 'v1/cart/removeadditionalcharge/:cartId', mainItemId: args.lineItemId}));       
            }
        }
      }),

      updateQuantity: builder.mutation({
        async queryFn(data, { dispatch, getState }, _extraOptions, baseQuery) {
          const url = `v1/carts/${data.cartId}/editquantity${data?.isV2Product ? '?isV2Product=true' : ''}`;
          const response: any = await baseQuery({
            url, method: 'POST', body: data.lineItems,
          });
          return response;
        },
        async onQueryStarted(args, { dispatch, getState, queryFulfilled }) {
            try{
                const {data} = await queryFulfilled;
                const {applicationState: {user}}: any = getState();
                const cart = getCart(getState() as any, {cartId: user.cartId, userId: user.userId, isLoggedIn: user.isLoggedIn})?.data ?? {};
                const cartObj = new Cart(data?.data?.attributes);
                let newCart: any = JSON.parse(JSON.stringify(cartObj));
                dispatch(newCartApi.util.updateQueryData('getCartByCartId', {cartId: user.cartId, userId: user.userId, isLoggedIn: user.isLoggedIn}, (draft) => Object.assign({}, newCart)));
                let specialOfferCookie = getBrandKeywordCampaignParameters(getCookie('np.brandKeywordCampaign')).couponCode;
                if(!specialOfferCookie){
                  dispatch(setValidatedCoupon({ state: CouponState.INITIAL}))
                }
                if(cart?.validDiscountCode || specialOfferCookie){
                  await dispatch(discountApi.endpoints.validateCoupon.initiate({ couponCode: specialOfferCookie || cart?.validDiscountCode, 'locale': config('locale') , callApplyCoupon: true}));
                }
                if(args?.isAccessory){
                  dispatch(onSuccessHandler(CartOperation.ADDACCESSORY, {mainItemId: args?.mainItemId,
                    itemId : args?.accessoryId,
                  }));
                }
                else{
                  dispatch(onSuccessHandler(CartOperation.UPDATEQUANTITY, {lineItemId: args.lineItems.data.attributes.lineItemId}));
                }
            }catch(error){
              console.log(error);
              dispatch(onErrorHandler({operation: CartOperation.UPDATEQUANTITY, err: JSON.stringify(error), errMsg: 'Error: while updating quantity', api: 'v1/carts/:cartId/editquantity', mainItemId: args?.lineItems?.data?.attributes?.lineItemId}));     
            }
        }
      }),
      
      addManual: builder.mutation({
        async queryFn({cartId, lineItemId, additionalChargeCode}, {dispatch, getState}, _extraOptions,  baseQuery) {
          const response:any = await baseQuery({ url: `v1/carts/${cartId}/customLineItems`, method: 'POST', body: { 
            "data": {
                "type": "AdditionalCharge",
                "attributes": {
                    "mainItemId": lineItemId,
                    "codes": [additionalChargeCode]
                }
            }
        }});
          return response;
        },
        async onQueryStarted(args, { dispatch, getState, queryFulfilled }) {
            try{
                const {data} = await queryFulfilled;
                const {applicationState: {user}}: any = getState();
                const oldCart = getCart(getState() as any, {cartId: user.cartId, userId: user.userId, isLoggedIn: user.isLoggedIn})?.data ?? {};
                let newCart:any = new Cart(data?.data?.attributes);
                dispatch(newCartApi.util.updateQueryData('getCartByCartId', {cartId: user.cartId, userId: user.userId, isLoggedIn: user.isLoggedIn}, (draft) => Object.assign({}, newCart)));
                let specialOfferCookie = getBrandKeywordCampaignParameters(getCookie('np.brandKeywordCampaign')).couponCode;
                if(!specialOfferCookie){
                  dispatch(setValidatedCoupon({ state: CouponState.INITIAL}))
                }
                if(oldCart?.validDiscountCode || specialOfferCookie){
                  await dispatch(discountApi.endpoints.validateCoupon.initiate({ couponCode: specialOfferCookie || oldCart?.validDiscountCode , 'locale': config('locale') , callApplyCoupon: true}));
                }
                dispatch(setCartOperation({mainItemId : args?.lineItemId}));
                dispatch(onSuccessHandler(CartOperation.ADDMANUAL, {}));
            }catch(error){
                console.log(error);
                dispatch(setCartOperation({mainItemId : args.lineItemId}));
                dispatch(onErrorHandler({operation: CartOperation.ADDMANUAL, err: JSON.stringify(error), errMsg: 'Error: while adding manual charge', api: 'v1/carts/:cartId/customLineItems', mainItemId: args?.lineItemId}));
            }
        }
      }),

      addAccessory : builder.mutation({
        async queryFn({cartId,mainItemId,quantity,productKey,variantId,accessoryId}, { dispatch, getState }, _extraOptions, baseQuery) {
          const response: any = await baseQuery({
            url: `v1/carts/${cartId}/lineitems`, method: 'POST', body: {
              "data": { 
                  "type": "Accessory",
                  "attributes": {
                      "mainItemId": mainItemId,
                      "quantity" : Number(quantity),
                      "productKey" : productKey,
                      "variantId" : variantId,
                  }
              }
          }});
          return response;
        },
        async onQueryStarted(args, { dispatch, getState, queryFulfilled }) {
          try{
              const {data} = await queryFulfilled;
              const {applicationState: {user}}: any = getState();
              const cart = getCart(getState() as any, {cartId: user.cartId, userId: user.userId, isLoggedIn: user.isLoggedIn})?.data ?? {};
              let newCart:any = new Cart(data?.data?.attributes);
              const lineItems = data?.data?.attributes.lineItems || [];
              if(cart.validDiscountCode) newCart.validDiscountCode = cart.validDiscountCode;
              dispatch(newCartApi.util.updateQueryData('getCartByCartId', {cartId: user.cartId, userId: user.userId, isLoggedIn: user.isLoggedIn}, (draft) => Object.assign({}, newCart)));
              let specialOfferCookie = getBrandKeywordCampaignParameters(getCookie('np.brandKeywordCampaign')).couponCode;
              if(!specialOfferCookie){
                dispatch(setValidatedCoupon({ state: CouponState.INITIAL}))
              }
              if(cart?.validDiscountCode || specialOfferCookie){
                dispatch(setCurrentOperation({name : CartOperation.ADDACCESSORY,mainItemId: args?.mainItemId,itemId: args?.accessoryId}))
                await dispatch(discountApi.endpoints.validateCoupon.initiate({ couponCode: specialOfferCookie || cart?.validDiscountCode , 'locale': config('locale') , callApplyCoupon: true}));
              }
              dispatch(onSuccessHandler(CartOperation.ADDACCESSORY, {mainItemId: args?.mainItemId,
                itemId : args?.accessoryId,accessoryLineItemId: lineItems[lineItems.length - 1]?.id,
              }));
            }catch(error){
              dispatch(onErrorHandler({operation: CartOperation.ADDACCESSORY, err: JSON.stringify(error), errMsg: 'Error: while adding Accessory', api: 'v1/carts/:cartId/lineitems', mainItemId: args.mainItemId}));  
            }
        }
      }),
      updateCart: builder.mutation({
        async queryFn({ cartId, actions, isExpressCheckout = false, setUserType = false }, { dispatch, getState }, _extraOptions, baseQuery) {
          const { applicationState: { user, customerStatus } }: any = getState();

          const isLoggedIn: boolean = user.isLoggedIn === 'true';

          // Add usertype header if setUserType is true
          // Set to 'loggedIn' for logged-in users, else use customerStatus or default to 'guest'
          const headers: Record<string, string> = {};
          if (setUserType) {
              const userType = isLoggedIn ? UserStatus.LOGGEDIN : (customerStatus || UserStatus.GUEST);
              headers["usertype"] = userType;
          }
    
          const response: any = await baseQuery({
            url: `v1/carts/${cartId}/updatecartdata`,
            method: 'POST',
            headers,
            body: {
              "data": {
                  "type": "cart",
                  "attributes": {
                      "actions": actions,
                      ...(isExpressCheckout ? { "methodOfPayment": "PaypalExpress" } : {})
                  }
              }
            }
          });
          return response;
        },
        async onQueryStarted(args, { dispatch , queryFulfilled }) {
            try{
                const {data} = await queryFulfilled;
                return data;
            }catch(error :any){
              const errorObj = {cartId : args.cartId, actions : args.actions, error};
              console.log("Error while updating cart data ",errorObj);
              dispatch(onErrorHandler({operation: CartOperation.UPDATECART, err: JSON.stringify(errorObj), errMsg: Array.isArray(error?.error?.data?.errors?.[0].title) ? 'Error: While updating cart data' : error?.error?.data?.errors?.[0]?.title, api: 'v1/carts/:cartId/updatecartdata'}));     
            }
        }
      }),
      addCoupon: builder.mutation({
        query: ({cartId, isAutoApplyCall = false,discountCode,isCouponManuallyApplied = false}) => ({
            url : `v1/carts/${cartId}/coupon`,
            method: 'POST',
            body: {
              "data": {
                "type": "Cart",
                "attributes": {
                  "discountCode" : discountCode
                }
              }
            }
        }),
        async onQueryStarted(args, { dispatch, queryFulfilled }) {
            try{
                dispatch(startLoading());
                const {data} = await queryFulfilled;
                await dispatch(addCouponSuccessHandler(data, args?.isAutoApplyCall, args?.isCouponManuallyApplied));
            }
            catch(error:any){
              const errorData = error?.error?.data?.errors;
              if (Array.isArray(errorData) && errorData[0]?.code === 400 && errorData[0]?.description?.includes('The cart is not in Active state.')) {
                console.log('Home page redirection due to following error in addCoupon API ', JSON.stringify(error));
                redirectToURL(`/${config('locale_details').current_sitename}/`);
              }
              else console.log(error);
              errorTrackingGaEvent('cart','Error while applying coupon');
              dispatch(onErrorHandler({operation: CartOperation.ADDCOUPON, err: JSON.stringify(error), errMsg: 'Error: while adding coupon', api: 'v1/carts/:cartId/coupon'}));       
            }
        }
      }),
      removeCoupon: builder.mutation({
        query: ({cartId, discountCode}) => ({
            url : `v1/carts/${cartId}/coupon`,
            method: 'DELETE',
            body: {
              "data": {
                "type": "Cart",
                "attributes": {
                  "discountCode": discountCode,
                }
              }
            }
        }),
        async onQueryStarted(args, { dispatch,getState, queryFulfilled }) {
            try{
                const{data} = await queryFulfilled;
                const {applicationState: {user}}: any = getState();
                const cart = getCart(getState() as any, {cartId: user.cartId, userId: user.userId, isLoggedIn: user.isLoggedIn})?.data ?? {};
                let newCart:any = new Cart(data?.data?.attributes);
                dispatch(setIsShippingDisc(false))
                dispatch(setValidatedCoupon({ state: CouponState.INITIAL}))
                dispatch(newCartApi.util.updateQueryData('getCartByCartId', {cartId: user.cartId, userId: user.userId, isLoggedIn: user.isLoggedIn}, (draft) => Object.assign({}, newCart)));
                dispatch(stopLoading());
                dispatch(onSuccessHandler(CartOperation.REMOVECOUPON, {discountCode : args.discountCode}));
            }
            catch(error){
                console.log(error);
                errorTrackingGaEvent('cart','Error while removing coupon');
                dispatch(onErrorHandler({operation: CartOperation.REMOVECOUPON, err: JSON.stringify(error), errMsg: 'Error: while removing coupon', api: 'v1/carts/:cartId/coupon'}));          
            }
        }
      }),
      getCartValidityForOrderConversion: builder.query({
        query: ({ cartId }) => ({
          url: `/v1/carts/${cartId}/validfororderconversion`,
          method: 'GET'
        }),
        async onQueryStarted(obj, { dispatch, getState, queryFulfilled }) {
          try{
              const {data} = await queryFulfilled;
              return data;
          }
          catch(error){
              console.log(error);
              return error;
          }
        }
      }),

      getCartForCustomer: builder.mutation({
        query: ({userId, ...params}) => ({
            url : `v1/carts/cust/${userId}`,
            method: 'GET',
            params: params
        }),
        async onQueryStarted(args, { dispatch, queryFulfilled }) {
            try{
                const {data} = await queryFulfilled;
                return data;
            }
            catch(error){
                console.log(error);
                dispatch(onErrorHandler({operation: 'getCartForCustomer', err: JSON.stringify(error), errMsg: 'Error: while fetching cart for customer', api: 'v1/cart/cust/:userId'}));     
            }
        }
    }),
    }), 
})

export const {
              useGetCartByCartIdQuery,
              useMarkCartInvisibleMutation,
              useRemoveItemMutation,
              useAddUpsellMutation,
              useRemoveManualMutation,
              useUpdateQuantityMutation,
              useAddAccessoryMutation,
              useAddManualMutation,
              useUpdateCartMutation,
              useRemoveCouponMutation,
              useAddCouponMutation,
              useGetCartValidityForOrderConversionQuery,
              useGetCartForCustomerMutation
              } = newCartApi;

export const getCart = (state:RootState, params) => newCartApi.endpoints.getCartByCartId.select(params)(state);